import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { Service, TimeSlot } from '@minibook/shared';
import {
  NAME_VALIDATION_ERROR_CODES,
  buildClientBirthDate,
} from '@minibook/shared';
import {
  createPublicAppointment,
  type AppointmentWithManageToken,
  type CreateAppointmentDto,
} from '../../api/bookingApi.js';
import type { ApiError } from '../../api/client.js';
import { validateBookingClientForm } from '../../bookingClientValidation.js';
import { resolveRateLimitRetryAfterSec } from '../../rateLimit.js';
import { parseVkUserId } from '../bookingSlotUtils.js';

type ClientBirthDate = { day: string; month: string; year: string };

type UseBookingSubmitParams = {
  slug: string | null;
  navigate: NavigateFunction;
  t: TFunction;
  selectedServices: Service[];
  selectedProducts: string[];
  selectedSlot: TimeSlot | null;
  effectiveClientEmail: string;
  firstName: string;
  lastName: string;
  middleName: string;
  clientPhoneE164: string;
  clientGender: 'male' | 'female' | '';
  clientBirthDate: ClientBirthDate;
  comment: string;
  vkId: string;
  notifyChannel: 'email';
};

export function useBookingSubmit({
  slug,
  navigate,
  t,
  selectedServices,
  selectedProducts,
  selectedSlot,
  effectiveClientEmail,
  firstName,
  lastName,
  middleName,
  clientPhoneE164,
  clientGender,
  clientBirthDate,
  comment,
  vkId,
  notifyChannel,
}: UseBookingSubmitParams) {
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);
  const [lastPayload, setLastPayload] = useState<CreateAppointmentDto | null>(null);
  const submittingRef = useRef(false);

  const navigateToSuccess = useCallback(
    (appointment: AppointmentWithManageToken) => {
      if (!slug) return;
      const params = new URLSearchParams({ appointmentId: appointment.id });
      if (appointment.manageToken) params.set('token', appointment.manageToken);
      if (appointment.manageShortCode) params.set('shortCode', appointment.manageShortCode);
      navigate(`/booking/${slug}/success?${params.toString()}`, {
        state: {
          appointment: {
            ...appointment,
            orderedServices: selectedServices.map((s) => ({
              name: s.name,
              durationMin: s.durationMin,
              price: s.price,
            })),
          },
          clientEmail: effectiveClientEmail.trim() || undefined,
        },
      });
    },
    [effectiveClientEmail, navigate, selectedServices, slug]
  );

  const handleCreateAppointmentError = useCallback(
    (err: unknown, setError: (message: string | null) => void) => {
      const apiError = err as ApiError;
      const retryAfterSec = resolveRateLimitRetryAfterSec(apiError);
      if (retryAfterSec !== null) {
        setRateLimitRetryAfter(retryAfterSec);
        setError(null);
        return;
      }
      const code = apiError.error?.code;
      if (code === NAME_VALIDATION_ERROR_CODES.NAME_REQUIRED) {
        setNameError(t('booking.client.nameOrSurnameRequired'));
        setError(null);
        setRateLimitRetryAfter(null);
        return;
      }
      if (code === NAME_VALIDATION_ERROR_CODES.INVALID_FIRST_NAME) {
        setNameError(t('booking.client.invalidFirstName'));
        setError(null);
        setRateLimitRetryAfter(null);
        return;
      }
      if (code === NAME_VALIDATION_ERROR_CODES.INVALID_LAST_NAME) {
        setNameError(t('booking.client.invalidLastName'));
        setError(null);
        setRateLimitRetryAfter(null);
        return;
      }
      const slotErrorMessages: Record<string, string> = {
        OUTSIDE_WORK_HOURS: t('booking.error.outsideWorkHours'),
        NO_SLOT_FOR_DURATION: t('booking.error.noSlotForDuration'),
        OVERLAP: t('booking.error.overlap'),
        RESOURCE_UNAVAILABLE: t('booking.error.resourceUnavailable'),
      };
      const message =
        slotErrorMessages[code] ?? apiError.error?.message ?? t('booking.error.createAppointment');
      setError(message);
      setRateLimitRetryAfter(null);
    },
    [t]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent, setError: (message: string | null) => void) => {
      e.preventDefault();
      if (submittingRef.current) return;
      if (!slug || !selectedSlot || selectedServices.length === 0) return;

      const validation = validateBookingClientForm({
        firstName,
        lastName,
        middleName,
        phoneE164: clientPhoneE164,
        email: effectiveClientEmail,
      });
      if (!validation.ok) {
        if (validation.field === 'name') {
          setNameError(
            validation.code === 'name_required'
              ? t('booking.client.nameOrSurnameRequired')
              : t('booking.client.nameCharsOnly')
          );
          return;
        }
        if (validation.field === 'phone') {
          setError(t('booking.error.invalidPhone'));
          return;
        }
        setEmailError(t('booking.client.emailInvalidOptional'));
        return;
      }
      setNameError(null);
      setEmailError(null);

      submittingRef.current = true;
      setSubmitting(true);
      setError(null);

      try {
        const birthDateResult = buildClientBirthDate(clientBirthDate);
        const birthDateIso = birthDateResult.ok ? (birthDateResult.value ?? undefined) : undefined;
        const payload = {
          serviceIds: selectedServices.map((s) => s.id),
          productIds: selectedProducts.length > 0 ? selectedProducts : undefined,
          startAt: selectedSlot.startAt,
          clientName: validation.fullName,
          clientPhone: validation.phoneForBackend,
          clientEmail: effectiveClientEmail.trim() || undefined,
          comment: comment.trim() || undefined,
          notifyChannel,
          vkUserId: parseVkUserId(vkId),
          gender: clientGender || undefined,
          birthDate: birthDateIso,
        };
        setLastPayload(payload);
        const appointment = await createPublicAppointment(slug, payload);
        navigateToSuccess(appointment);
      } catch (err) {
        handleCreateAppointmentError(err, setError);
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [
      clientBirthDate,
      clientGender,
      clientPhoneE164,
      comment,
      effectiveClientEmail,
      firstName,
      handleCreateAppointmentError,
      lastName,
      middleName,
      navigateToSuccess,
      notifyChannel,
      selectedProducts,
      selectedServices,
      selectedSlot,
      slug,
      t,
      vkId,
    ]
  );

  const handleRetry = useCallback(
    async (setError: (message: string | null) => void) => {
      if (!slug || !lastPayload) return;
      if (rateLimitRetryAfter !== null && rateLimitRetryAfter > 0) return;

      setRateLimitRetryAfter(null);
      setSubmitting(true);
      setError(null);

      try {
        const appointment = await createPublicAppointment(slug, lastPayload);
        navigateToSuccess(appointment);
      } catch (err) {
        handleCreateAppointmentError(err, setError);
      } finally {
        setSubmitting(false);
      }
    },
    [handleCreateAppointmentError, lastPayload, navigateToSuccess, rateLimitRetryAfter, slug]
  );

  useEffect(() => {
    if (rateLimitRetryAfter === null || rateLimitRetryAfter <= 0) return;

    const timer = setInterval(() => {
      setRateLimitRetryAfter((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rateLimitRetryAfter]);

  return {
    submitting,
    nameError,
    setNameError,
    emailError,
    setEmailError,
    rateLimitRetryAfter,
    setRateLimitRetryAfter,
    handleSubmit,
    handleRetry,
  };
}
